"use client";

import { useState } from "react";
import Image from "next/image";
import clsx from "clsx";

// The Api's own /api/media-file proxy serves full-resolution originals (2-3 MB
// JPEGs). Those go through Next's optimizer (AVIF/WebP at the displayed width);
// `handleError` below falls back to the raw URL if the optimizer can't fetch it.
const API_HOST = (() => {
  try {
    return process.env.NEXT_PUBLIC_API_URL ? new URL(process.env.NEXT_PUBLIC_API_URL).host : "";
  } catch {
    return "";
  }
})();

function canOptimize(src: string) {
  if (!src || src.startsWith("data:") || src.startsWith("blob:")) return false;
  if (API_HOST && /^https?:\/\//i.test(src)) {
    try {
      if (new URL(src).host === API_HOST) return !src.split("?")[0].toLowerCase().endsWith(".svg");
    } catch {}
  }
  // Next's `/_next/image` optimizer fetches the upstream with a ~7s abort.
  // Parspack/MinIO originals often miss that window from this host, which
  // dumps `TimeoutError` in PM2 and delays the photo until we fall back.
  // The browser can hit the CDN directly; skip the Node proxy for http(s).
  if (/^https?:\/\//i.test(src)) return false;
  const path = src.split("?")[0].toLowerCase();
  return !path.endsWith(".svg");
}

/**
 * Drop-in image with a big.dk-style blur-up placeholder:
 * a tiny inline JPEG (`blurDataUrl`) is shown immediately, heavily blurred,
 * then crossfades into the real image. Local raster files still go through
 * Next's optimizer (AVIF/WebP + width). Remote CDN photos load directly —
 * proxying them through `/_next/image` on this host times out.
 *
 * `fill`: true when the caller already provides a sized, positioned
 * (relative/absolute) parent box to fill (e.g. an aspect-ratio div driven
 * by JS, like ProjectRow's hero) — the wrapper itself becomes `absolute
 * inset-0` instead of establishing its own aspect-ratio box.
 */
export function BlurImage({
  src,
  alt,
  width,
  height,
  blurDataUrl,
  className,
  wrapperClassName,
  loading = "lazy",
  fetchPriority = "auto",
  draggable = true,
  fill = false,
  sizes,
  onLoad,
}: {
  src: string;
  alt: string;
  width: number;
  height: number;
  blurDataUrl?: string;
  className?: string;
  wrapperClassName?: string;
  loading?: "lazy" | "eager";
  fetchPriority?: "high" | "low" | "auto";
  draggable?: boolean;
  fill?: boolean;
  sizes?: string;
  onLoad?: () => void;
}) {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [optimized, setOptimized] = useState(() => canOptimize(src));
  const [activeSrc, setActiveSrc] = useState(src);
  if (src !== activeSrc) {
    setActiveSrc(src);
    setOptimized(canOptimize(src));
    setLoaded(false);
    setFailed(false);
  }
  const safeWidth = width > 0 ? width : 16;
  const safeHeight = height > 0 ? height : 10;
  const priority = fetchPriority === "high";
  const showFull = loaded && !failed;

  const handleLoad = () => {
    setLoaded(true);
    onLoad?.();
  };

  const handleError = () => {
    if (optimized) {
      setOptimized(false);
      setLoaded(false);
      return;
    }
    setFailed(true);
  };

  return (
    <span
      className={clsx("block overflow-hidden", fill ? "absolute inset-0" : "relative w-full", wrapperClassName)}
      style={fill ? undefined : { aspectRatio: `${safeWidth} / ${safeHeight}` }}
    >
      {blurDataUrl ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          aria-hidden
          alt=""
          src={blurDataUrl}
          className={clsx(
            "pointer-events-none absolute inset-0 h-full w-full scale-110 object-cover transition-opacity duration-700 ease-out",
            showFull ? "opacity-0" : "opacity-100",
          )}
          style={{ filter: "blur(20px)" }}
          draggable={false}
        />
      ) : (
        <span
          aria-hidden
          className={clsx(
            "absolute inset-0 animate-pulse bg-neutral-200 transition-opacity duration-500 ease-out",
            showFull ? "opacity-0" : "opacity-100",
          )}
        />
      )}
      <Image
        key={optimized ? "opt" : "raw"}
        src={src}
        alt={alt}
        fill
        sizes={sizes ?? "100vw"}
        quality={75}
        unoptimized={!optimized}
        priority={priority}
        loading={priority ? undefined : loading}
        draggable={draggable}
        onLoad={handleLoad}
        onError={handleError}
        className={clsx("object-cover", failed && "opacity-0", className)}
      />
    </span>
  );
}
